import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BsFileEarmarkPdfFill, BsFillFileEarmarkWordFill } from 'react-icons/bs'

import { formatBytes } from '../../utils'

import DeleteModal, { DeleteModalHandle } from '@/components/common/delete-modal'
import { IDocument } from '@interfaces/document'
import { TableHead, TableRow, TableHeader, TableCell, TableBody, Table } from '@/components/ui/table'

import { FileIcon, Ellipsis, UploadIcon, FilePlus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import DateDisplay from './date-display'
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react'
import { useBilling } from '@/hooks/useBilling'

export interface DocumentListProps {
  documents: IDocument[]
  onDocumentDeleted?: (id: string) => void
}

const DocumentList = ({ documents, onDocumentDeleted }: DocumentListProps) => {
  const { active, onSubscribe, trialingAndCappedDocs } = useBilling()

  const [selectedDocument, setSelectedDocument] = useState<IDocument | null>(null)
  const dialogRef = useRef<DeleteModalHandle>(null)

  const navigate = useNavigate()

  const renderIcon = (file_extension: 'docx' | 'pdf' = 'docx') => {
    if (!file_extension) file_extension = 'docx'

    switch (file_extension) {
      case 'pdf':
        return <BsFileEarmarkPdfFill className="h-5 w-5 text-red-500 dark:text-red-400" />
      case 'docx':
        return <BsFillFileEarmarkWordFill className="h-5 w-5 text-blue-500 dark:text-blue-400" />
    }
  }

  const handleNavigation = (path: string) => {
    if (trialingAndCappedDocs) {
      onSubscribe('TRIAL_LIMIT_DOCS_COUNT')
      return
    }

    if (!active) {
      onSubscribe()
      return
    }

    navigate(path)
  }

  return (
    <>
      <Card>
        <CardHeader className="px-7">
          <div className="flex items-center justify-between">
            <CardTitle>Recent Files</CardTitle>

            <div className="flex gap-2">
              <Button
                color="primary"
                className="text-white"
                title="Start with a blank document"
                startContent={<FilePlus className="text-white" />}
                onPress={() => handleNavigation('/editor')}
              >
                Blank
              </Button>
              <Button
                color="primary"
                title="Upload a document"
                startContent={<UploadIcon />}
                onPress={() => handleNavigation('/upload')}
              >
                Upload
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[32px]">
                      <FileIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </TableHead>
                    <TableHead>File</TableHead>
                    <TableHead className="text-right">Modified</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableCell colSpan={5} className="py-12 text-center text-gray-500">
                      <p className="mb-2 text-lg font-medium">Upload a document</p>
                      <p className="text-sm">
                        Turn any document into something you can edit, review, send, and act on.
                      </p>
                    </TableCell>
                  ) : (
                    documents.map((item: IDocument, key: number) => (
                      <TableRow key={key}>
                        <TableCell>{renderIcon(item.file_extension as 'docx' | 'pdf')}</TableCell>
                        <TableCell className="font-medium">
                          <Link className="hover:underline" to={`/review/${item.public_id}`}>
                            {item.file_name}
                            {item.file_extension && `.${item.file_extension}`}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right">
                          <DateDisplay datetime={item.updated_at} />
                        </TableCell>
                        <TableCell className="text-right">
                          {item.file_size ? formatBytes(item.file_size, 0) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Dropdown>
                            <DropdownTrigger>
                              <Button variant="bordered">
                                <Ellipsis className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                              onAction={key => {
                                switch (key) {
                                  case 'view':
                                    return navigate(`/review/${item.public_id}`)
                                  case 'delete':
                                    setSelectedDocument(item)
                                    dialogRef.current?.open()
                                    return
                                  default:
                                    break
                                }
                              }}
                            >
                              <DropdownItem key="view">Open</DropdownItem>
                              <DropdownItem key="delete" className="text-danger" color="danger">
                                Remove
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteModal
        ref={dialogRef}
        confirmText="Are you sure you want to delete this document?"
        deleteText="Delete"
        cancelText="Cancel"
        onDeleteClick={() => {
          if (onDocumentDeleted && selectedDocument) {
            onDocumentDeleted(selectedDocument.public_id)
          }
        }}
      />
    </>
  )
}

export default DocumentList
